import React, { useMemo, useState, useEffect, useRef } from "react";
import { Calculator, Plane, Ship, MessageCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { SERVICE_FEE_PCT } from "@/data/rates";
import { useShippingRates } from "@/hooks/useShippingRates";
import { buildWhatsAppUrl } from "@/data/translations";
import { trackEvent } from "@/lib/analytics";

const methodIcon = (id) => (id === "sea" ? Ship : Plane);

export default function ShippingCalculator({ country }) {
  const { t, lang, isRTL } = useLanguage();
  const C = t.calculator;

  // Single source of truth: admin DB via /api/content → adapted to the
  // calculator shape. Falls back to the static rates.js mirror if the
  // API is unreachable so the calculator never goes blank.
  const { rates: ALL_RATES } = useShippingRates();
  const rates = ALL_RATES[country.code];
  const methods = rates?.methods || [];

  const [methodId, setMethodId] = useState(methods[0]?.id || "");
  const method = methods.find((m) => m.id === methodId) || methods[0];
  const [catId, setCatId] = useState(method?.categories[0]?.id || "");
  const category = method?.categories.find((c) => c.id === catId) || method?.categories[0];
  const hasTrackedViewRef = useRef(false);
  const previousMethodRef = useRef(methodId);
  const calculationSignatureRef = useRef("");

  // When method changes, reset category
  useEffect(() => {
    if (method && !method.categories.find((c) => c.id === catId)) {
      setCatId(method.categories[0]?.id || "");
    }
  }, [methodId]); // eslint-disable-line react-hooks/exhaustive-deps

  // When rates load asynchronously (API → adapted shape), the available
  // method/category IDs may differ from the static fallback we used on
  // mount. Re-sync the selected method/category to the first available
  // option whenever the underlying list changes.
  useEffect(() => {
    if (methods.length === 0) return;
    if (!methods.find((m) => m.id === methodId)) {
      const firstMethod = methods[0];
      setMethodId(firstMethod.id);
      setCatId(firstMethod.categories[0]?.id || "");
      return;
    }
    const curMethod = methods.find((m) => m.id === methodId);
    if (curMethod && !curMethod.categories.find((c) => c.id === catId)) {
      setCatId(curMethod.categories[0]?.id || "");
    }
  }, [methods]); // eslint-disable-line react-hooks/exhaustive-deps

  const [qty, setQty] = useState("");
  const [invoice, setInvoice] = useState("");

  const isCbm = (category?.unit || method?.unit) === "cbm";
  const hasSecondary = Boolean(category?.secondary_price && category?.secondary_unit);
  // For combined rates we need TWO inputs (primary unit qty + secondary
  // unit qty). qty drives the primary unit; qty2 drives the secondary.
  const [qty2, setQty2] = useState("");
  // Reset qty2 when the category changes (avoid carrying stale values).
  useEffect(() => { setQty2(""); }, [category?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const qtyNum = parseFloat(qty) || 0;
  const qty2Num = parseFloat(qty2) || 0;
  const invoiceNum = parseFloat(invoice) || 0;

  const shipping = useMemo(() => {
    if (!category) return 0;
    const primary = (category.price || 0) * qtyNum;
    const secondary = hasSecondary ? (category.secondary_price || 0) * qty2Num : 0;
    return primary + secondary;
  }, [category, qtyNum, qty2Num, hasSecondary]);

  const serviceFee = useMemo(() => {
    return (invoiceNum * SERVICE_FEE_PCT) / 100;
  }, [invoiceNum]);

  const total = shipping + serviceFee;

  const countryName = lang === "ar" ? country.name_ar : lang === "fr" ? country.name_en : country.name_en;
  const nameField = `name_${lang}`;
  const transitField = `transit_${lang}`;

  const waMsg = `${t.common.whatsappCountryMsg(countryName)}\n${C.method}: ${method?.[nameField] || method?.name_en || ""}\n${C.category}: ${category?.[nameField] || category?.name_en || ""}\n${isCbm ? C.volume : C.weight}: ${qtyNum} ${isCbm ? C.volumeUnit : C.weightUnit}${hasSecondary ? `\n${category?.secondary_unit === "cbm" ? C.volume : C.weight}: ${qty2Num} ${category?.secondary_unit === "cbm" ? C.volumeUnit : C.weightUnit}` : ""}\n${C.invoice}: $${invoiceNum}\n${C.total}: $${total.toFixed(2)}`;

  useEffect(() => {
    if (hasTrackedViewRef.current) return;
    hasTrackedViewRef.current = true;
    trackEvent("shipping_calculator_view", {
      routeName: "shipping_calculator",
      country: { code: country.code, name: countryName },
      shipping: { method: method?.id, category: category?.id, unit: category?.unit || method?.unit },
    });
  }, [category?.id, category?.unit, country.code, countryName, method?.id, method?.unit]);

  useEffect(() => {
    if (previousMethodRef.current === methodId) return;
    previousMethodRef.current = methodId;
    trackEvent("shipping_method_select", {
      routeName: "shipping_calculator",
      country: { code: country.code, name: countryName },
      shipping: {
        method: method?.id,
        category: category?.id,
        unit: category?.unit || method?.unit,
      },
    });
  }, [category?.id, category?.unit, country.code, countryName, method?.id, method?.unit, methodId]);

  useEffect(() => {
    if (qtyNum <= 0 && (!hasSecondary || qty2Num <= 0)) return undefined;
    const signature = [country.code, method?.id, category?.id, qtyNum, qty2Num, invoiceNum].join("|");
    if (calculationSignatureRef.current === signature) return undefined;
    const timer = window.setTimeout(() => {
      calculationSignatureRef.current = signature;
      trackEvent("shipping_calculator_calculate", {
        routeName: "shipping_calculator",
        country: { code: country.code, name: countryName },
        shipping: {
          method: method?.id,
          category: category?.id,
          unit: category?.unit || method?.unit,
          quantity: qtyNum,
          secondaryQuantity: qty2Num,
          invoiceValue: invoiceNum,
          total,
        },
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [category?.id, category?.unit, country.code, countryName, hasSecondary, invoiceNum, method?.id, method?.unit, qty2Num, qtyNum, total]);

  if (!rates || methods.length === 0) return null;

  return (
    <section
      id="calculator"
      className="relative overflow-hidden rounded-[30px] border border-black/6 bg-white p-6 shadow-[0_18px_50px_rgba(16,24,40,0.08)] sm:p-10 md:rounded-[32px] md:p-12 dark:border-white/10 dark:bg-[#121212]"
      data-testid="shipping-calculator"
    >
      <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-[#F37021]/10 blur-[100px] pointer-events-none" />

      <div className="relative grid lg:grid-cols-12 gap-8 md:gap-10">
        {/* Left: info */}
        <div className="lg:col-span-5">
          <span className="inline-flex items-center gap-2 rounded-full border border-[#F37021]/18 bg-[#FFF3EA] px-3.5 py-1.5 text-xs font-semibold tracking-[0.3em] uppercase text-[#C95D13] mb-3">
            <Calculator className="w-4 h-4" />
            — {C.eyebrow}
          </span>
          <h2 className="mb-4 font-display text-3xl font-bold leading-[1.08] text-[#111] sm:text-4xl md:text-5xl dark:text-white">
            {C.title}
          </h2>
          <p className="max-w-md text-sm leading-relaxed text-[#555] sm:text-base md:text-lg dark:text-[#BAC4D4]">
            {C.subtitle}
          </p>

          {/* Method toggles - quick visual */}
          <div className="mt-6 flex flex-wrap gap-2">
            {methods.map((m) => {
              const Icon = methodIcon(m.id);
              const active = m.id === methodId;
              return (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => setMethodId(m.id)}
                  className={`inline-flex items-center gap-2 h-11 px-4 rounded-full text-sm font-medium border transition-all ${
                    active
                      ? "bg-[#111] text-white border-[#111] shadow-[0_12px_26px_rgba(17,17,17,0.12)]"
                      : "bg-white text-[#444] border-black/10 hover:border-[#F37021] hover:text-[#F37021] hover:-translate-y-0.5 dark:border-white/10 dark:bg-[#1B1B1B] dark:text-[#DDE5F2] dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                  }`}
                  data-testid={`calc-method-${m.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {m[nameField] || m.name_en}
                </button>
              );
            })}
          </div>

          {method && (
            <div className="mt-5 inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-[#FDF1E8] text-[#C55810] text-xs font-semibold tracking-wide uppercase">
              {C.transit}: {method[transitField] || method.transit_en}
            </div>
          )}
        </div>

        {/* Right: inputs */}
        <div className="lg:col-span-7">
          <div className="space-y-4 rounded-[28px] border border-black/6 bg-[#FAFAFA] p-4 md:p-5 dark:border-white/10 dark:bg-[#121212]">
            {/* Category */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
                {C.category}
              </label>
              <div className="flex flex-wrap gap-2">
                {method?.categories.map((c) => {
                  const active = c.id === catId;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setCatId(c.id)}
                      className={`inline-flex items-center gap-2 h-11 px-4 rounded-full text-sm font-medium border transition-all ${
                        active
                          ? "bg-[#F37021] text-white border-[#F37021] shadow-[0_12px_26px_rgba(243,112,33,0.2)]"
                          : "bg-white text-[#444] border-black/10 hover:border-[#F37021] hover:text-[#F37021] hover:-translate-y-0.5 dark:border-white/10 dark:bg-[#1B1B1B] dark:text-[#DDE5F2] dark:hover:border-[#F8A056] dark:hover:text-[#F8A056]"
                      }`}
                      data-testid={`calc-cat-${c.id}`}
                    >
                      {c[nameField] || c.name_en}
                      <span className={`text-[11px] font-semibold ${active ? "text-white/80" : "text-[#F37021]"}`}>
                        ${c.price}/{(c.unit || method?.unit) === "cbm" ? "m³" : "kg"}
                        {c.secondary_price ? ` + $${c.secondary_price}/${c.secondary_unit === "cbm" ? "m³" : "kg"}` : ""}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Weight / Volume */}
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
                  {isCbm ? C.volume : C.weight}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    dir="ltr"
                    value={qty}
                    onChange={(e) => setQty(e.target.value)}
                    placeholder={isCbm ? "1.5" : "2.0"}
                    className={`w-full h-12 rounded-2xl border border-black/10 bg-white px-4 text-sm transition-all shadow-[0_10px_24px_rgba(16,24,40,0.03)] focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:border-white/10 dark:bg-[#121212] dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}
                    data-testid="calc-input-qty"
                  />
                  <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wider text-[#888] dark:text-[#8FA1B8]">
                    {isCbm ? C.volumeUnit : C.weightUnit}
                  </span>
                </div>
              </div>
              {/* Secondary unit input — only shown for combined rates
                  (e.g. CN sea clothes/shoes = $200/m³ + $5.5/kg). */}
              {hasSecondary && (
                <div data-testid="calc-input-qty2-wrapper">
                  <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
                    {category.secondary_unit === "cbm" ? C.volume : C.weight}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      dir="ltr"
                      value={qty2}
                      onChange={(e) => setQty2(e.target.value)}
                      placeholder={category.secondary_unit === "cbm" ? "1.5" : "2.0"}
                      className={`w-full h-12 rounded-2xl border border-black/10 bg-white px-4 text-sm transition-all shadow-[0_10px_24px_rgba(16,24,40,0.03)] focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:border-white/10 dark:bg-[#121212] dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}
                      data-testid="calc-input-qty2"
                    />
                    <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wider text-[#888] dark:text-[#8FA1B8]">
                      {category.secondary_unit === "cbm" ? C.volumeUnit : C.weightUnit}
                    </span>
                  </div>
                </div>
              )}
              <div>
                <label className="mb-2 block text-xs font-semibold uppercase tracking-wide text-[#555] dark:text-[#BAC4D4]">
                  {C.invoice}
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="0"
                    step="1"
                    dir="ltr"
                    value={invoice}
                    onChange={(e) => setInvoice(e.target.value)}
                    placeholder="250"
                    className={`w-full h-12 rounded-2xl border border-black/10 bg-white px-4 text-sm transition-all shadow-[0_10px_24px_rgba(16,24,40,0.03)] focus:border-[#F37021] focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#F37021]/20 dark:border-white/10 dark:bg-[#121212] dark:text-white ${isRTL ? 'text-right' : 'text-left'}`}
                    data-testid="calc-input-invoice"
                  />
                  <span className="pointer-events-none absolute end-4 top-1/2 -translate-y-1/2 text-xs font-semibold uppercase tracking-wider text-[#888] dark:text-[#8FA1B8]">
                    USD
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-[#888] dark:text-[#8FA1B8]">{C.invoiceHint}</p>
              </div>
            </div>

            {/* Result */}
            <motion.div
              key={`${methodId}-${catId}-${qtyNum}-${invoiceNum}`}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25 }}
              className="mt-4 p-5 md:p-6 rounded-[24px] bg-[#111] text-white shadow-[0_22px_60px_rgba(16,24,40,0.18)]"
              data-testid="calc-result"
            >
              {qtyNum <= 0 && (!hasSecondary || qty2Num <= 0) ? (
                <p className="text-sm text-[#A3A3A3]">{C.enterWeight}</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-[#A3A3A3]">{C.shippingCost}</span>
                    <span className="font-semibold tabular-nums">${shipping.toFixed(2)}</span>
                  </div>
                  {invoiceNum > 0 && (
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-[#A3A3A3]">{C.serviceFee}</span>
                      <span className="font-semibold tabular-nums">${serviceFee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="h-px bg-white/10" />
                  <div className="flex justify-between items-center">
                    <span className="text-sm tracking-wider uppercase text-[#F37021] font-semibold">
                      {C.total}
                    </span>
                    <span className="font-display text-2xl md:text-3xl font-bold tabular-nums">
                      ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              )}
            </motion.div>

            <p className="text-[11px] text-[#888] dark:text-[#8FA1B8]">{C.disclaimer}</p>

            <a
              href={buildWhatsAppUrl(waMsg)}
              onClick={() => {
                trackEvent("whatsapp_click", {
                  routeName: "shipping_calculator",
                  country: { code: country.code, name: countryName },
                  shipping: {
                    method: method?.id,
                    category: category?.id,
                    unit: category?.unit || method?.unit,
                    quantity: qtyNum,
                    secondaryQuantity: qty2Num,
                    invoiceValue: invoiceNum,
                    total,
                  },
                  metadata: { source: "shipping_calculator" },
                });
                trackEvent("contact_click", {
                  routeName: "shipping_calculator",
                  country: { code: country.code, name: countryName },
                  metadata: { source: "shipping_calculator", channel: "whatsapp" },
                });
              }}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full h-14 rounded-full bg-[#25D366] hover:bg-[#1EBE5C] text-white font-medium flex items-center justify-center gap-2 transition-all hover:-translate-y-0.5 shadow-[0_12px_30px_rgba(37,211,102,0.25)]"
              data-testid="calc-order-cta"
            >
              <MessageCircle className="w-5 h-5" />
              {C.orderCta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
